import { Button } from "~/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle
} from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage
} from "./ui/form";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { animated, useSpring } from "react-spring";
import { PASSWORD_PATTERN } from "~/lib/auth-utils";

const formSchema = z
	.object({
		password: z.string().regex(PASSWORD_PATTERN, {
			message:
				"Use at least 8 characters with upper and lowercase letters, a number, and a symbol."
		}),
		confirm_password: z.string()
	})
	.refine((values) => values.password === values.confirm_password, {
		message: "Passwords do not match.",
		path: ["confirm_password"]
	});

type props = {
	submit: (password: string) => void;
	isLoading: boolean;
};

export function SetNewPasswordForm({ submit, isLoading }: props) {
	const initAnimation = useSpring({
		from: {
			opacity: "0%",
			y: "50%"
		},
		to: {
			opacity: "100%",
			y: "0%"
		},
		config: {
			tension: 150,
			trail: 5000
		}
	});

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			password: "",
			confirm_password: ""
		},
		reValidateMode: "onBlur"
	});

	async function onSubmit(values: z.infer<typeof formSchema>) {
		submit(values.password);
	}

	return (
		<animated.div style={initAnimation}>
			<Card className="w-full max-w-sm">
				<CardHeader>
					<CardTitle className="text-2xl">Set your password</CardTitle>
					<CardDescription>
						Choose a new password to finish setting up your account. You will
						use this password from now on.
					</CardDescription>
				</CardHeader>
				<CardContent className="grid gap-4">
					<Form {...form}>
						<form
							method="POST"
							onSubmit={form.handleSubmit(onSubmit)}
							className="grid gap-4"
						>
							<FormField
								control={form.control}
								name="password"
								render={({ field }) => (
									<FormItem className="grid gap-2">
										<FormLabel htmlFor="new-password">New password</FormLabel>
										<FormControl>
											<Input
												{...field}
												id="new-password"
												type="password"
												autoComplete="new-password"
												required
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="confirm_password"
								render={({ field }) => (
									<FormItem className="grid gap-2">
										<FormLabel htmlFor="confirm-password">
											Confirm password
										</FormLabel>
										<FormControl>
											<Input
												{...field}
												id="confirm-password"
												type="password"
												autoComplete="new-password"
												required
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<Button
								disabled={isLoading}
								className="w-full"
								type="submit"
							>
								{isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
								Save password and continue
							</Button>
						</form>
					</Form>
				</CardContent>
			</Card>
		</animated.div>
	);
}
